import OngletCritèresFicheFormation from "./OngletCritèresFicheFormation/OngletCritèresFicheFormation";
import OngletFormationFicheFormation from "./OngletFormationFicheFormation/OngletFormationFicheFormation";
import { type OngletsFicheFormationProps } from "./OngletsFicheFormation.interface";
import useOngletsFicheFormation from "./useOngletsFicheFormation.tsx";
import { i18n } from "@/configuration/i18n/i18n";
import { Tabs } from "@codegouvfr/react-dsfr/Tabs";
import { useEffect, useRef } from "react";

const OngletsFicheFormation = ({ formation }: OngletsFicheFormationProps) => {
  const ongletsIds = ["onglet_details", "onglet_criteres"];
  const référenceOnglets = useRef<HTMLDivElement>(null);

  const { changementOngletFicheFormation } = useOngletsFicheFormation({
    formation,
    onglets: ongletsIds,
  });

  useEffect(() => {
    const élémentOnglets = référenceOnglets.current;
    if (!élémentOnglets) return undefined;

    let frameId: number | undefined;

    const mettreÀJourHauteurDesOnglets = () => {
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = undefined;

        const listeOnglets = élémentOnglets.querySelector<HTMLElement>(".fr-tabs__list");
        const panneauSélectionné = élémentOnglets.querySelector<HTMLElement>(".fr-tabs__panel--selected");

        if (!listeOnglets || !panneauSélectionné) return;

        const hauteurOnglets = `${listeOnglets.offsetHeight + panneauSélectionné.offsetHeight}px`;

        if (élémentOnglets.style.getPropertyValue("--tabs-height") !== hauteurOnglets) {
          élémentOnglets.style.setProperty("--tabs-height", hauteurOnglets);
        }
      });
    };

    mettreÀJourHauteurDesOnglets();

    const resizeObserver =
      typeof window.ResizeObserver === "undefined"
        ? undefined
        : new window.ResizeObserver(mettreÀJourHauteurDesOnglets);
    const mutationObserver =
      typeof window.MutationObserver === "undefined"
        ? undefined
        : new window.MutationObserver(mettreÀJourHauteurDesOnglets);

    for (const élément of élémentOnglets.querySelectorAll<HTMLElement>(".fr-tabs__list, .fr-tabs__panel")) {
      resizeObserver?.observe(élément);
    }

    mutationObserver?.observe(élémentOnglets, {
      attributeFilter: ["class", "style"],
      attributes: true,
      subtree: true,
    });

    window.addEventListener("resize", mettreÀJourHauteurDesOnglets);

    return () => {
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
      }

      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", mettreÀJourHauteurDesOnglets);
    };
  }, []);

  const générerLesOnglets = () => {
    const onglets = [];

    if ((formation.descriptifs.formation && formation.descriptifs.formation !== "") || formation.liens.length > 0) {
      onglets.push({
        label: i18n.PAGE_FORMATION.ONGLET_FORMATION,
        content: (
          <OngletFormationFicheFormation
            id={formation.id}
            liens={formation.liens}
            texte={formation.descriptifs.formation}
          />
        ),
      });
    }

    if (
      (formation.descriptifs.attendus && formation.descriptifs.attendus !== "") ||
      formation.critèresAnalyse.length > 0 ||
      formation.admis.répartition.parBac.length > 0 ||
      formation.admis.moyenneGénérale.centiles.length > 0
    ) {
      onglets.push({
        label: i18n.PAGE_FORMATION.ONGLET_CRITÈRES,
        content: (
          <OngletCritèresFicheFormation
            critèresAnalyse={formation.critèresAnalyse}
            descriptifAttendus={formation.descriptifs.attendus}
            moyenneGénérale={formation.admis.moyenneGénérale}
            répartitionParBac={formation.admis.répartition.parBac}
          />
        ),
      });
    }

    return onglets;
  };

  return (
    <Tabs
      label={i18n.ACCESSIBILITÉ.ONGLETS_FORMATION}
      onTabChange={changementOngletFicheFormation}
      ref={référenceOnglets}
      tabs={générerLesOnglets()}
    />
  );
};

export default OngletsFicheFormation;
